import React, { useState, useEffect } from 'react';
import { Joyride, STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';

const ONBOARDING_KEY = 'cerp_master_onboarding_completed';

// Custom styles for the tour
const joyrideStyles = {
  options: {
    arrowColor: '#fff',
    backgroundColor: '#fff',
    overlayColor: 'rgba(30, 58, 95, 0.7)',
    primaryColor: '#0d9488',
    spotlightShadow: '0 0 15px rgba(13, 148, 136, 0.5)',
    textColor: '#1e3a5f',
    zIndex: 10000,
  },
  buttonNext: {
    backgroundColor: '#0d9488',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '14px',
    padding: '8px 16px',
  },
  buttonBack: {
    color: '#64748b',
    fontSize: '14px',
    marginRight: 10,
  },
  buttonSkip: {
    color: '#94a3b8',
    fontSize: '14px',
  },
  tooltip: {
    borderRadius: '16px',
    padding: '20px',
  },
  tooltipTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    fontFamily: 'Manrope, sans-serif',
    color: '#1e3a5f',
  },
  tooltipContent: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#475569',
  },
  beacon: {
    inner: '#0d9488',
    outer: '#0d9488',
  },
};

// Tour steps definition
const masterTourSteps = [
  {
    target: 'body',
    content: (
      <div className="text-center py-4">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-[#1e3a5f] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Bem-vindo ao Plano Master! 🎉
        </h2>
        <p className="text-gray-600">
          Você agora tem acesso ao gerenciamento de <strong>múltiplas empresas (Multi-CNPJ)</strong>. 
          Vamos fazer um tour rápido pelas novas funcionalidades!
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
    title: '',
  },
  {
    target: '[data-testid="company-switcher-trigger"]',
    content: (
      <div>
        <p className="mb-3">
          Este é o <strong>Seletor de Empresa</strong>. Com ele você pode:
        </p>
        <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
          <li>Ver qual empresa está ativa no momento</li>
          <li>Alternar entre suas empresas cadastradas</li>
          <li>Adicionar novas empresas rapidamente</li>
        </ul>
        <p className="mt-3 text-sm text-[#0d9488] font-medium">
          💡 Clique para abrir o dropdown e explorar!
        </p>
      </div>
    ),
    title: '🏢 Seletor de Empresa',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-master-dashboard"]',
    content: (
      <div>
        <p className="mb-3">
          O <strong>Master Dashboard</strong> oferece uma visão consolidada de todas as suas empresas:
        </p>
        <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
          <li>KPIs agregados de todas as empresas</li>
          <li>Distribuição de riscos consolidada</li>
          <li>Gestão completa de CNPJs</li>
          <li>Relatórios corporativos</li>
        </ul>
      </div>
    ),
    title: '📊 Dashboard Master',
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: '[data-testid="nav-assinatura"]',
    content: (
      <div>
        <p className="mb-3">
          Na página de <strong>Assinatura</strong> você pode:
        </p>
        <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
          <li>Visualizar seu plano atual</li>
          <li>Fazer upgrade ou renovação</li>
          <li>Consultar histórico de pagamentos</li>
          <li>Gerenciar dados de faturamento</li>
        </ul>
      </div>
    ),
    title: '💳 Assinatura',
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: 'body',
    content: (
      <div className="text-center py-4">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#0d9488] to-[#1e3a5f] flex items-center justify-center">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-[#1e3a5f] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Tudo pronto! ✨
        </h2>
        <p className="text-gray-600 mb-4">
          Agora você está pronto para gerenciar todas as suas empresas em um só lugar.
        </p>
        <div className="bg-[#e8f4fc] rounded-xl p-4 text-left">
          <p className="text-sm font-medium text-[#1e3a5f] mb-2">Próximos passos sugeridos:</p>
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
            <li>Acesse o Master Dashboard</li>
            <li>Adicione uma nova empresa</li>
            <li>Explore os relatórios consolidados</li>
          </ol>
        </div>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
    title: '',
  },
];

export default function MasterOnboarding() {
  const { isMaster, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [runTour, setRunTour] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    // DISABLED: Automatic tour triggering was blocking UI interactions
    // The tour can be manually triggered by calling resetMasterOnboarding(userId)
    // and then setting a state to show the tour
    
    // Check if user is Master and hasn't completed onboarding
    if (isMaster && user) {
      const onboardingCompleted = localStorage.getItem(`${ONBOARDING_KEY}_${user.id}`);
      
      // Only show tour on dashboard - DISABLED FOR NOW
      // Users can manually start the tour from settings or a help button
      if (!onboardingCompleted && location.pathname === '/') {
        // Mark as completed to prevent blocking - user can restart manually
        localStorage.setItem(`${ONBOARDING_KEY}_${user.id}`, 'true');
        
        // Don't auto-start the tour anymore
        // const timer = setTimeout(() => {
        //   setRunTour(true);
        // }, 1500);
        // return () => clearTimeout(timer);
      }
    }
  }, [isMaster, user, location.pathname]);

  const handleJoyrideCallback = (data) => {
    const { status, action, index, type } = data;
    
    // Handle step changes
    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    }
    
    // Handle tour completion or skip
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      if (user) {
        localStorage.setItem(`${ONBOARDING_KEY}_${user.id}`, 'true');
      }
      
      // If finished (not skipped), navigate to Master Dashboard
      if (status === STATUS.FINISHED) {
        navigate('/master');
      }
    }
  };

  // Don't render if not master or tour shouldn't run
  if (!isMaster || !runTour) {
    return null;
  }

  return (
    <Joyride
      steps={masterTourSteps}
      run={runTour}
      stepIndex={stepIndex}
      continuous
      showProgress
      showSkipButton
      hideCloseButton={false}
      disableOverlayClose={false}
      disableScrolling={false}
      spotlightClicks={true}
      disableOverlay={true}
      callback={handleJoyrideCallback}
      styles={joyrideStyles}
      locale={{
        back: 'Voltar',
        close: 'Fechar',
        last: 'Começar!',
        next: 'Próximo',
        skip: 'Pular tour',
      }}
      floaterProps={{
        disableAnimation: false,
      }}
    />
  );
}

// Export function to manually trigger tour
export const resetMasterOnboarding = (userId) => {
  if (userId) {
    localStorage.removeItem(`${ONBOARDING_KEY}_${userId}`);
  }
};

// Export function to check if onboarding is completed
export const isMasterOnboardingCompleted = (userId) => {
  if (userId) {
    return localStorage.getItem(`${ONBOARDING_KEY}_${userId}`) === 'true';
  }
  return false;
};
